import { TitleCasePipe } from '@angular/common';

import { BillingParties, QuoteMilestone, contains } from '@library/common';
import { MilestoneConst, QuotationMode, QuotationModes, QuotationType, QuotationTypes } from '@library/common/models';
import { Filter, FilterBy } from '@library/modules/sidebar';
import { IGenericType } from '@library/common/generictype';

const milestoneConst = MilestoneConst();

export const latestMilestone = ( s: IGenericType ) => {
    if ( !s?.milestones ) { console.log('found undefined', s); }

    return milestoneConst.filter( m => s?.milestones[ m.code ] )
        .slice( -1 )[0] || new QuoteMilestone();
};


const filterByField = ( list: IGenericType[], field: string ): FilterBy[] => {
    return list.map( s => s[ field ] as string )
        .filter(( v, i, a ) => a.indexOf( v ) === i )
        .filter( _ => _ )
        .map( code => ({
            code: [ code ],
            label: [ code ],
            active: false,
            count: list.filter( s => s[ field ] === code ).length,
        }));
};

const filterByPartyField = ( list: IGenericType[], party: BillingParties, field: string, value?: string ): FilterBy[] => {
    const mapped = list.map( s => s[ party ][ field ] as string );
    const filtered = mapped.filter( p => {
        if ( !p ) { return false; }

        return contains( p, value );
    });

    return filtered
        .filter(( v, i, a ) => a.indexOf( v ) === i )
        .sort()
        .map( code => ({
            code: [ code ],
            label: [ code ],
            active: false,
            count: filtered.filter( p => p === code ).length,
        }));
};

const quotationFilter = ( field: string, title: string ): Filter<IGenericType> => {
    return ({
        id: field,
        order: 0,
        title: `Quotations By ${title}`,
        filterBy: list => filterByField( list, field ),
        filterList: ( list, value ) => value ? list.filter( l => l[field] === value ) : list
    });
};

const quotationPartyFilter = ( party: BillingParties, field: string, title: string ): Filter<IGenericType> => {
    return ({
        id: party + field,
        order: 0,
        title: `Quotations By ${title}`,
        matchValue: true,
        filterBy: ( list, value? ) => filterByPartyField( list, party, field, value ),
        filterList: ( list, value, data? ) => list
           .filter( s => !value ? true : s[party][field] === value )
           .filter( s => !data ? true : contains( s[party][field] as string, data )),
     });
};

const quotationReferenceFilter = ( party: BillingParties, title: string ): Filter<IGenericType> => {
    return ({
        id: party + 'reference',
        order: 0,
        title: 'Quotations By ' + title,
        matchValue: true,
        filterBy: ( list, value? ) => {
            const refs = list.map( s => s.references || [] )
                .reduce(( a, c ) => [ ...a, ...c ], [] )
                .filter( r => r.party === party )
                .map( r => ( r.type ? r.type.toUpperCase() + ' - ' : '' ) + r.data )
                .filter( r => !value ? r : contains( r, value ));

            return refs
                .filter(( v, i, a ) => a.indexOf( v ) === i )
                .sort()
                .map( code => ({
                code: [ code ],
                label: [ code ],
                active: false,
                count: refs.filter( s => s === code ).length,
                }));
        },
        filterList: ( list, code ) =>
            list.filter( s => !code ? s : s.references.filter( r => r.party === party )
                .filter( r => ( r.type ? r.type.toUpperCase() + ' - ' : '' ) + r.data === code ).length ),
    });
}

export const MainItems: FilterBy[] = [
    { label: [ 'Total Quotation', 'Total Quotations' ], code: [], svgIcon: 'total' },
    {
        label: [ 'New Quotation', 'New Quotations' ],
        code: [ 'submitted' ],
        svgIcon: 'new',
        tooltip: 'Quote',
    },
    {
        label: [ 'Active Quote', 'Active Quotes' ],
        code: [ 'submitted', 'ready' ],
        svgIcon: 'active',
        tooltip: 'Active',
    },
    {
        label: [ 'Accepted' ],
        code: [ 'accepted' ],
        svgIcon: 'complete',
        tooltip: 'Accepted',
    },
    {
        label: [ 'Rejected' ],
        code: [ 'rejected' ],
        svgIcon: 'complete',
        tooltip: 'Rejected',
    },
    { label: [ 'Expired' ], code: [ 'expired' ], svgIcon: 'complete', tooltip: 'expired' },
];


export const MainFilter: Filter<IGenericType> = {
    id: 'main',
    order: 0,
    title: 'Summary',
    filterBy: list => {
        if ( !list.length ) { return; }

        const milestones = list.map( s => latestMilestone( s ).code ) || [];

        MainItems.map( tile => {
            tile.count = milestones.length;
            if ( tile.code.length ) {
                tile.count = milestones.filter( m => tile.code.includes( m )).length;
            }
        });

        return MainItems;
    },
    filterList: ( list, value ) => value ? list.filter( l => latestMilestone( l ).code === value ) : list,
};

export const ByMode: Filter<IGenericType> = ({
    ...quotationFilter( 'mode', 'Mode' ),
    order: 0,
    hard: true,
    filterBy: list => filterByField( list, 'mode' )
        .sort(( a, b ) => QuotationModes.indexOf( a.code[0] as QuotationMode ) - QuotationModes.indexOf( b.code[0] as QuotationMode ))
        .map( v => ({ ...v, label: v.label.map( s => new TitleCasePipe().transform( s )), svgIcon: v.code[0] as string }))
});

export const ByType: Filter<IGenericType> = ({
    ...quotationFilter( 'quoteType', 'Type' ),
    order: 0,
    hard: true,
    filterBy: list => filterByField( list, 'quoteType' )
       .sort(( a, b ) => QuotationTypes.indexOf( a.code[0] as QuotationType ) - QuotationTypes.indexOf( b.code[0] as QuotationType ))
       .map( v => ({ ...v, label:  v.label.map( s => new TitleCasePipe().transform( s )), svgIcon: v.code[0] }))
});

export const ByStatus: Filter<IGenericType> = {
    id: 'milestone',
    order: 0,
    title: 'Quotations By Status',
    filterBy: list => {
        const statuses: FilterBy[] = [];
        const milestones = list.map( s => latestMilestone( s ).code );

        milestoneConst.map( m => {
            const mtotal = milestones.filter( milestone => milestone === m.code ).length;
            if ( mtotal > 0 ) {
                statuses.push({
                code: [ m.code ],
                count: mtotal,
                label: [ m.label ],
                svgIcon: m.icon
                });
            }
        });

        return statuses;
    },
    filterList: ( list, value ) => value ? list.filter( l => latestMilestone( l ).code === value ) : list,
};

export const ByServiceLevel: Filter<IGenericType> = quotationFilter( 'serviceLevel', 'Service Level' );

export const ByShipperName: Filter<IGenericType> = quotationPartyFilter( 'shipper', 'name', 'Shipper Name' );

export const ByConsigneeName: Filter<IGenericType> = quotationPartyFilter( 'consignee', 'name', 'Consignee Name' );

export const ByShipperReference: Filter<IGenericType> = quotationReferenceFilter( 'shipper', 'Shipper Reference' );

export const ByConsigneeReference: Filter<IGenericType> = quotationReferenceFilter( 'consignee', 'Consignee Reference' );

export const QuotationFilters: Filter<IGenericType>[] = [
    ByType,
    ByMode,
    MainFilter,
    ByServiceLevel,
    ByStatus,
    ByShipperName,
    ByConsigneeName,
    ByShipperReference,
    ByConsigneeReference,
];
